<template>
  <b-modal
    id="modal-order-view"
    ref="modal"
    v-model="open"
    size="ov-custom"
    centered
    hide-footer
    title="Manage Status"
    @hidden="onModalClose"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="manageStatus"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickManageStatus)"
        >
          <div class="my-1 font-weight-bold">
            Basic information
          </div>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Status Name"
                label-for="h-name"
              >
                <b-form-input
                  id="name"
                  v-model="statusOptions.name"
                  placeholder="Enter Name"
                  type="text"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Custom Name"
                label-for="h-customName"
              >
                <b-form-input
                  id="customName"
                  v-model="customStatusName"
                  placeholder="Enter Custom Name"
                  type="text"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Category"
                label-for="h-category"
              >
                <v-select
                  v-model="statusOptions.category"
                  :options="categoryOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Category"
                  :clearable="false"
                >
                  <template v-slot:option="option">
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Color"
                label-for="h-color"
              >
                <v-select
                  v-model="statusOptions.color"
                  :options="colorOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Color"
                  class="color-options-dropdown"
                  :clearable="false"
                >
                  <template v-slot:selected-option="option">
                    <div class="status-color-option selected">
                      <span
                        class="color-dot"
                        :style="{ backgroundColor: option.color || option.value || option }"
                      />
                      {{ option.name || option.value || option }}
                    </div>
                  </template>
                  <template v-slot:option="option">
                    <div
                      class="status-color-option"
                      :style="{ '--option-color': option.color }"
                    >
                      <span
                        class="color-dot"
                        :style="{ backgroundColor: option.color }"
                      />
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-form-group
                class="required status-form-label"
                label="Description"
                label-for="h-description"
              >
                <b-form-textarea
                  id="description"
                  v-model="statusOptions.description"
                  placeholder="Enter Description"
                  type="text"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="my-1 font-weight-bold">
            Status configuration
          </div>
          <b-row v-if="!isCustomStatus">
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Before Default Statuses"
                label-for="h-beforeDefaultStatuses"
              >
                <v-select
                  v-model="statusOptions.before_default_statuses"
                  :options="beforeDefaultStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  :deselect-from-dropdown="false"
                  class="manage-status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="After Default Statuses"
                label-for="h-afterDefaultStatuses"
              >
                <v-select
                  v-model="statusOptions.after_default_statuses"
                  :options="afterDefaultStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  :deselect-from-dropdown="false"
                  class="manage-status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Before Custom Statuses"
                label-for="h-beforeStatuses"
              >
                <v-select
                  v-model="statusOptions.before_custom_statuses"
                  :options="beforeCustomStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="After Custom Statuses"
                label-for="h-afterStatuses"
              >
                <v-select
                  v-model="statusOptions.after_custom_statuses"
                  :options="afterCustomStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Icon"
                label-for="h-icon"
              >
                <icon-picker v-model="statusOptions.icon" />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="my-1 font-weight-bold">
            Status settings
          </div>
          <b-row class="mt-1">
            <b-col cols="6">
              <b-form-checkbox
                v-model="statusOptions.is_default"
                :value="true"
                :unchecked-value="false"
              >
                Default Status
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="statusOptions.key === 'key_1' || isCustomStatus"
              cols="6"
            >
              <b-form-checkbox
                v-model="statusOptions.is_active"
                :value="true"
                :unchecked-value="false"
              >
                Active Status
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="!(statusOptions.key === 'key_1' || statusOptions.key === 'key_14' || statusOptions.key === 'key_26' || statusOptions.key === 'key_28' || statusOptions.key === 'key_29')"
              cols="6"
            >
              <b-form-checkbox
                v-model="statusOptions.is_bulk_update"
                :value="true"
                :unchecked-value="false"
              >
                Allow Bulk Status Update
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="statusOptions.key === 'key_29'"
              cols="6"
            >
              <b-form-checkbox
                v-model="statusOptions.settings.allow_false_update_from"
                :value="true"
                :unchecked-value="false"
              >
                Allow Direct Transition To Received At Destination Warehouse
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="(statusOptions.key === 'key_15' || statusOptions.key === 'key_17' || statusOptions.key === 'key_31')"
              cols="6"
            >
              <b-form-group
                label="Remember Reason"
                label-for="checkbox-remember-reason"
                label-cols-md="3"
              >
                <b-form-checkbox
                  id="checkbox-remember-reason"
                  v-model="statusOptions.is_remember_reason_enabled"
                  class="mb-3"
                >
                  Enable Remember Reason
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Bypass & Attempt Limits Section (Added) -->
          <div v-if="isCustomStatus">
            <div class="my-1 font-weight-bold">
              Bypass &amp; Attempt Limits
            </div>
            <div class="bypass-container p-2 mb-2">
              <div class="d-flex align-items-center mb-1">
                <b-form-checkbox
                  id="allow-bypass-checkbox"
                  v-model="statusOptions.is_bypass_enabled"
                  :value="true"
                  :unchecked-value="false"
                  class="mb-0"
                >
                  <span class="font-weight-bold">Allow Bypass</span>
                </b-form-checkbox>
                <feather-icon
                  id="tooltip-allow-bypass"
                  icon="HelpCircleIcon"
                  size="16"
                  class="text-muted ml-50 cursor-pointer"
                />
                <b-tooltip
                  target="tooltip-allow-bypass"
                  triggers="hover"
                >
                  Turn this on to let this status be skipped, and set attempt limits for how many times an order can enter this status.
                </b-tooltip>
              </div>

              <!-- Maximum Attempt Dropdown (Visible only when Allow Bypass is checked) -->
              <b-col
                v-if="statusOptions.is_bypass_enabled"
                cols="6"
                md="3"
                class="mt-1 pl-0"
              >
                <b-form-group
                  label="Maximum Attempt"
                  label-for="max-attempt-select"
                  class="status-form-label mb-0"
                >
                  <v-select
                    id="max-attempt-select"
                    v-model="statusOptions.max_attempts"
                    :options="maxAttemptOptions"
                    :reduce="option => option.value"
                    label="name"
                    placeholder="Select Attempt"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
              <small class="form-text text-muted mt-50">
                How many times a transaction may pass through this status before it must move on.
              </small>
            </div>
          </div>

          <b-col
            v-if="statusOptions.key === 'key_11' || isCustomStatus"
            class="my-2 d-flex justify-content-center"
          >
            <b-button
              variant="text-secondary"
              class="p-0 mb-1"
              @click="showMore = !showMore"
            >
              {{ showMore ? 'See less' : 'See more' }}
              <feather-icon
                :icon="showMore ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="15"
              />
            </b-button>
          </b-col>
          <div v-if="showMore">
            <div v-if="statusOptions.key === 'key_11' || isCustomStatus">
              <div class="my-1 font-weight-bold">
                Validations
              </div>
              <b-row class="mt-1">
                <b-col cols="6">
                  <b-form-checkbox
                    v-model="statusOptions.settings.any_destination_branch_enabled"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow any destination branch
                  </b-form-checkbox>
                </b-col>
                <b-col
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.false_branch_update_enabled"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Branch Update
                  </b-form-checkbox>
                </b-col>
                <b-col
                  v-if="isCustomStatus"
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.allow_false_update_from"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Update From
                  </b-form-checkbox>
                </b-col>
                <b-col
                  v-if="isCustomStatus"
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.allow_false_update_to"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Update To
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
            <!-- Actions Section -->
            <div v-if="isCustomStatus && statusOptions.actions && Object.keys(statusOptions.actions).length > 0">
              <div class="my-1 font-weight-bold">
                Actions
              </div>
              <b-row>
                <b-col
                  v-for="(value, key) in statusOptions.actions"
                  :key="key"
                  md="4"
                >
                  <b-form-checkbox
                    v-model="statusOptions.actions[key]"
                  >
                    {{ formatInput(key) }}
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
          </div>
          <b-col
            class="mb-2 mt-2 d-flex justify-content-center"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              class="mr-1"
            >
              Submit
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="clearAll"
            >
              Clear
            </b-button>
          </b-col>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BOverlay,
  VBModal,
  BRow,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BFormCheckbox,
  BButton,
  BFormTextarea,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Ripple from 'vue-ripple-directive'
import IconPicker from '@/components/Icons/IconPicker.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import STATUS_COLOR_OPTIONS from '@/constants/statusColors'

const OrderRepository = RepositoryFactory.get('order')

export default {
  name: 'ManageStatus',
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BModal,
    BOverlay,
    BRow,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BFormCheckbox,
    BButton,
    BFormTextarea,
    BTooltip,
    IconPicker,
    vSelect,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      open: false,
      signatureOpen: false,
      modalLoading: false,
      loading: false,
      id: null,
      items: {},
      perPage: 10,
      statusOptions: {
        settings: {},
        actions: {},
        key: '',
        is_bypass_enabled: false,
        max_attempts: null,
      },
      maxAttemptOptions: [
        { value: -1, name: 'Any' },
        { value: 1, name: '1' },
        { value: 2, name: '2' },
        { value: 3, name: '3' },
        { value: 4, name: '4' },
        { value: 5, name: '5' },
      ],
      customStatusName: '',
      categoryOptions: [
        { name: 'First Mile', value: 'first' },
        { name: 'Middle Mile', value: 'middle' },
        { name: 'Last Mile', value: 'last' },
        { name: 'Merchant', value: 'merchant' },
      ],
      colorOptions: STATUS_COLOR_OPTIONS,
      beforeDefaultStatusOptions: [],
      afterDefaultStatusOptions: [],
      beforeCustomStatusOptions: [],
      afterCustomStatusOptions: [],
      permissionGroupOptions: [],
      showMore: false,
    }
  },
  computed: {
    filterobject() {
      return Object.keys(this.statusOptions.actions).filter(key => this.statusOptions.actions[key])
    },
    isCustomStatus() {
      return this.statusOptions.key.startsWith('cus_')
    },
  },
  methods: {
    formatInput(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
    async fetchStatusDetails() {
      try {
        const { data } = (await OrderRepository.updateStatusId(this.id)).data
        await this.fetchCustomBeforeAndAfterStatuses()
        if (!data.key.startsWith('cus_')) {
          await this.fetchDefaultBeforeAndAfterStatuses()
        }
        await this.$nextTick()
        this.statusOptions = {
          max_attempts: null,
          ...data,
        }
        if (!this.statusOptions.settings) {
          this.statusOptions.settings = {}
        }
        if (!this.statusOptions.actions) {
          this.statusOptions.actions = {}
        }
        this.customStatusName = this.statusOptions.name
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async onClickManageStatus() {
      try {
        this.loading = true
        if (!this.statusOptions.settings.any_destination_branch_enabled && this.statusOptions.settings.false_branch_update_enabled) {
          this.showErrorMessage("Cannot enable the 'False Branch Update' validation without selecting the 'Allow any destination branch' validation.")
          this.loading = false
          return
        }
        const data = {
          status_data: this.statusOptions,
          name: this.customStatusName,
          category: this.statusOptions.category,
          color: this.statusOptions.color,
          description: this.statusOptions.description,
          before_custom_statuses: this.statusOptions.before_custom_statuses,
          after_custom_statuses: this.statusOptions.after_custom_statuses,
          icon: this.statusOptions.icon,
          is_default: this.statusOptions.is_default,
          is_active: this.statusOptions.is_active,
          is_bypass_enabled: this.statusOptions.is_bypass_enabled,
          max_attempts: this.statusOptions.max_attempts,
          actions: this.statusOptions.actions,
          settings: {
            any_destination_branch_enabled: this.statusOptions.settings.any_destination_branch_enabled,
            false_branch_update_enabled: this.statusOptions.settings.false_branch_update_enabled,
            allow_false_update_from: this.statusOptions.settings.allow_false_update_from,
            allow_false_update_to: this.statusOptions.settings.allow_false_update_to,
          },
        }
        const res = await OrderRepository.updateStatusIdupdate(this.statusOptions.id, data)
        if (res.status === 200) {
          this.open = false
          this.showSuccessMessage(`${this.statusOptions.original_name}  updated successfully`)
          this.$parent.onClickRefresh()
          localStorage.removeItem('order_statuses')
          try {
            const statuses = (await OrderRepository.getFullStatusList()).data.data
            if (Array.isArray(statuses)) {
              localStorage.setItem('order_statuses', JSON.stringify(statuses))
              this.$store.commit('SET_ORDER_STATUS_LIST', statuses)
            }
          } catch (cacheError) {
            // Status save already succeeded; keep using the previous cached list.
          }
        } else {
          this.showErrorMessage(`Cannot Update ${this.statusOptions.original_name}  Status Settings`)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async openModal(id) {
      this.open = true
      this.id = id
      this.modalLoading = true
      await this.fetchStatusDetails()
      await this.readPermissionGroups()
      this.modalLoading = false
    },
    onModalClose() {
      this.showMore = false
    },
    clearAll() {
      this.statusOptions.is_active = false
      this.statusOptions.is_bypass_enabled = false
      this.statusOptions.max_attempts = null
      this.statusOptions.settings.any_destination_branch_enabled = false
      this.statusOptions.settings.false_branch_update_enabled = false
      this.statusOptions.settings.allow_false_update_from = false
      this.statusOptions.settings.allow_false_update_to = false
      this.customStatusName = ''
    },
    async fetchDefaultBeforeAndAfterStatuses() {
      const statusOptions = (await OrderRepository.getDefaultStatusDropdownList(this.id)).data
      this.beforeDefaultStatusOptions = statusOptions.before_default_statuses
      this.afterDefaultStatusOptions = statusOptions.after_default_statuses
    },
    async fetchCustomBeforeAndAfterStatuses() {
      const statusOptions = (await OrderRepository.getCustomStatusDropdownList(this.id)).data
      this.beforeCustomStatusOptions = statusOptions.before_custom_statuses
      this.afterCustomStatusOptions = statusOptions.after_custom_statuses
    },
    async readPermissionGroups() {
      this.permissionGroupOptions = (await OrderRepository.getStatusPermissionGroups()).data.data
    },
  },
}
</script>
<style scoped>
b-form-checkbox{
  margin-bottom: 10px;
}
  @media (min-width: 767px) {
    .status-bar {
      padding : 1rem;
    }
    .status-badge {
      display: inline-block;
      white-space: normal;
    }
  }
.custom-control {
    position: relative;
    z-index: 1;
    display: block;
    min-height: 1.45rem;
    margin-bottom: 15px;
  }
.text-container {
  max-width: 100%;
  word-wrap: break-word;
  white-space: normal;
}
.formCheckbox {
  margin-top: 8px;
}
.validation-label{
  color: #5e5873;
}
.color-options-dropdown ::v-deep .vs__dropdown-option {
  padding: 0 !important;
}
.status-color-option {
  padding: 6px 12px;
  display: flex;
  align-items: center;
}
.status-color-option.selected {
  padding: 0;
}
.color-options-dropdown ::v-deep .vs__dropdown-option--highlight .status-color-option {
  background-color: var(--option-color);
  color: white;
}
.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

/* Bypass Container Styling */
.bypass-container {
  border: 1px dashed #d8d6de;
  border-radius: 0.428rem;
  background-color: #fafafc;
}
</style>
